import { useState, useCallback, useEffect } from 'react';
import { unformatCPF, getCPFError } from '../utils';
import { PaymentType, PaymentStatus, ExecuteResponse } from '../api/payamigo';
import { usePayAmigoQuote } from './usePayAmigoQuote';
import { usePayAmigoPayment } from './usePayAmigoPayment';
import { usePayAmigoCoins } from './usePayAmigoCoins';

interface UsePayAmigoFormProps {
  sessionId: string | null;
  paymentType: PaymentType;
  initialCoin?: string;
}

interface UsePayAmigoFormResult {
  // Coins
  coins: Array<{ key: string; value: string }>;
  coinsLoading: boolean;
  coinsLoadingComplete: boolean;
  selectedCoin: string;
  
  // Form fields
  cpf: string;
  cpfError: string | null;
  amount: string;
  amountError: string | null;
  pixKey: string;
  pixKeyError: string | null;
  
  // Handlers
  handleCpfChange: (value: string) => void;
  handleAmountChange: (value: string) => void;
  handlePixKeyChange: (value: string) => void;
  handleCoinChange: (newValue: { key: string; value?: string }) => void;
  
  // Quote
  quote: any;
  quoteLoading: boolean;
  quoteError: string | null;
  
  // Payment
  executeResult: ExecuteResponse | null;
  currentStatus: PaymentStatus | null;
  isExecuting: boolean;
  executeError: string | null;
  handleExecutePayment: () => Promise<void>;
  
  // Validation
  isFormValid: boolean;
  isPaymentComplete: boolean;
  isPaymentFailed: boolean;
}

export const usePayAmigoForm = ({
  sessionId,
  paymentType,
  initialCoin,
}: UsePayAmigoFormProps): UsePayAmigoFormResult => {
  // Coins management
  const { coins, isLoading: coinsLoading, fetchCoins } = usePayAmigoCoins();
  const [selectedCoin, setSelectedCoin] = useState<string>('');
  const [coinsLoadingComplete, setCoinsLoadingComplete] = useState(false);

  // Quote management
  const { quote, isLoading: quoteLoading, error: quoteError, fetchQuote, clearQuote, isQuoteExpired } = usePayAmigoQuote();

  // Payment management
  const { executeResult, currentStatus, isExecuting, error: executeError, executePayment, clearPayment } = usePayAmigoPayment();

  // Form state
  const [cpf, setCpf] = useState('');
  const [cpfError, setCpfError] = useState<string | null>(null);
  const [cpfTouched, setCpfTouched] = useState(false); // Track if CPF was ever valid
  const [amount, setAmount] = useState('');
  const [amountError, setAmountError] = useState<string | null>(null);
  const [pixKey, setPixKey] = useState('');
  const [pixKeyError, setPixKeyError] = useState<string | null>(null);

  // Fetch coins when session is ready
  useEffect(() => {
    if (sessionId) {
      fetchCoins();
    }
  }, [sessionId, fetchCoins]);

  // Mark coins loading as complete
  useEffect(() => {
    if (!coinsLoading && sessionId) {
      setCoinsLoadingComplete(true);
    }
  }, [coinsLoading, sessionId]);

  // Set initial coin
  useEffect(() => {
    if (coins.length > 0 && !selectedCoin) {
      if (initialCoin && coins.some(c => c.key === initialCoin)) {
        setSelectedCoin(initialCoin);
      }
    }
  }, [coins, initialCoin, selectedCoin]);

  // Handle CPF change
  const handleCpfChange = useCallback((value: string) => {
    setCpf(value);
    const digitsOnly = unformatCPF(value);
    
    // Check if CPF is valid (complete and no errors)
    const isValid = digitsOnly.length === 11 && !getCPFError(value);
    
    // If valid, mark as touched so we show errors if user deletes chars later
    let shouldShowAllErrors = cpfTouched;
    if (isValid && !cpfTouched) {
      setCpfTouched(true);
      shouldShowAllErrors = true;
    }
    
    // Show all errors if CPF was touched (user had valid CPF before)
    const error = getCPFError(value, shouldShowAllErrors);
    setCpfError(error);
  }, [cpfTouched]);

  // Handle amount change
  const handleAmountChange = useCallback((value: string) => {
    // Allow only numbers and decimal point
    const sanitized = value.replace(/[^\d.]/g, '');
    
    // Prevent multiple decimal points
    const parts = sanitized.split('.');
    if (parts.length > 2) {
      return;
    }

    setAmount(sanitized);

    // Validate amount
    if (!sanitized || sanitized === '') {
      setAmountError('Amount is required');
      return;
    }

    const amountNum = parseFloat(sanitized);
    if (isNaN(amountNum) || amountNum <= 0) {
      setAmountError('Invalid amount');
      return;
    }

    setAmountError(null);
  }, []);

  // Handle PIX Key change (phone number only)
  const handlePixKeyChange = useCallback((value: string) => {
    // Allow only digits, spaces, +, -, (, )
    const sanitized = value.replace(/[^\d\s+()-]/g, '');
    setPixKey(sanitized);
    
    if (paymentType === PaymentType.Withdrawal) {
      if (!sanitized || sanitized.trim() === '') {
        setPixKeyError('PIX Key is required');
        return;
      }
      
      // Remove all non-digit characters for validation
      const digitsOnly = sanitized.replace(/\D/g, '');
      if (digitsOnly.length < 10 || digitsOnly.length > 15) {
        setPixKeyError('Invalid phone number');
        return;
      }
    }
    
    setPixKeyError(null);
  }, [paymentType]);

  // Handle coin change
  const handleCoinChange = useCallback((newValue: { key: string; value?: string }) => {
    setSelectedCoin(newValue.key);
    setAmount(''); // Clear amount on currency change
    setAmountError(null);
    clearQuote(); // Clear quote on currency change
  }, [clearQuote]);

  // Fetch quote when ALL fields are filled
  useEffect(() => {
    if (!sessionId || !selectedCoin || !cpf || !amount) {
      return;
    }

    if (paymentType === PaymentType.Withdrawal && !pixKey) {
      return;
    }

    const cpfDigits = unformatCPF(cpf);
    if (cpfDigits.length !== 11 || cpfError) {
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0 || amountError) {
      return;
    }

    fetchQuote({
      cpf_number: cpfDigits,
      amount: amount,
      coin: selectedCoin,
      ...(paymentType === PaymentType.Withdrawal && pixKey && { pix_key: pixKey }),
    });
  }, [sessionId, cpf, cpfError, amount, amountError, selectedCoin, pixKey, paymentType, fetchQuote]);

  // Auto-refresh expired quote if not executing
  useEffect(() => {
    if (isQuoteExpired && sessionId && selectedCoin && cpf && amount && !executeResult) {
      const cpfDigits = unformatCPF(cpf);
      if (cpfDigits.length === 11 && !cpfError && !amountError) {
        fetchQuote({
          cpf_number: cpfDigits,
          amount: amount,
          coin: selectedCoin,
          ...(paymentType === PaymentType.Withdrawal && pixKey && { pix_key: pixKey }),
        });
      }
    }
  }, [isQuoteExpired, sessionId, selectedCoin, cpf, cpfError, amount, amountError, pixKey, paymentType, executeResult, fetchQuote]);

  // Clear payment state when paymentType changes (only if there was an execution)
  useEffect(() => {
    if (executeResult) {
      clearPayment();
    }
  }, [paymentType]); // Don't include clearPayment and executeResult in deps to avoid loops

  // Handle payment execution
  const handleExecutePayment = useCallback(async () => {
    if (!quote) {
      return;
    }
    await executePayment(quote.quote_id);
  }, [quote, executePayment]);

  // Check if form is valid
  const isSessionExpired = quoteError?.includes('Session expired') || quoteError?.includes('not found') || 
                           executeError?.includes('Session expired') || executeError?.includes('not found');
  
  // Check if CPF is complete and valid
  const isCpfValid = cpf && unformatCPF(cpf).length === 11 && !cpfError;
  
  const isFormValid = isCpfValid && !amountError && amount && quote && !isSessionExpired &&
    (paymentType === PaymentType.Deposit || (paymentType === PaymentType.Withdrawal && pixKey && !pixKeyError));

  // Check if payment was successful
  const isPaymentComplete = currentStatus === PaymentStatus.PAYMENT_COMPLETED || currentStatus === PaymentStatus.CONVERSION_COMPLETED;
  const isPaymentFailed = currentStatus === PaymentStatus.PAYMENT_FAILED || currentStatus === PaymentStatus.CONVERSION_FAILED;

  return {
    // Coins
    coins,
    coinsLoading: Boolean(coinsLoading),
    coinsLoadingComplete: Boolean(coinsLoadingComplete),
    selectedCoin,
    
    // Form fields
    cpf,
    cpfError,
    amount,
    amountError,
    pixKey,
    pixKeyError,
    
    // Handlers
    handleCpfChange,
    handleAmountChange,
    handlePixKeyChange,
    handleCoinChange,
    
    // Quote
    quote,
    quoteLoading: Boolean(quoteLoading),
    quoteError,
    
    // Payment
    executeResult,
    currentStatus,
    isExecuting: Boolean(isExecuting),
    executeError,
    handleExecutePayment,
    
    // Validation
    isFormValid: Boolean(isFormValid),
    isPaymentComplete: Boolean(isPaymentComplete),
    isPaymentFailed: Boolean(isPaymentFailed),
  };
};
